import React from "react";
import IntegrationTimeline from "../../../../../../common/components/integration-timeline/IntegrationTimeline";
import { useTimelineStepPermissions } from "../../../../../../hooks/useTimelineStepPermissions";
import { Integration } from "../../../../../../redux/slices/projects/Types";
import { getOnboardingCurrentStatus } from "../integration-steps/onboarding";
import { getTrainingCurrentStatus } from "../integration-steps/training";
import { getDevelopmentCurrentStatus } from "../integration-steps/development";
import { getValidationCurrentStatus } from "../integration-steps/validation";
import {
  buildIntegrationMilestones,
  getTimelineCompletionPercent,
} from "../utils/buildIntegrationMilestones";
import IntegrationPhasePanel from "./IntegrationPhasePanel";
import "./IntegrationTimelineSection.scss";

const INTEGRATION_PHASES = [
  "Onboarding",
  "Training",
  "Development",
  "Validation",
] as const;

export type IntegrationPhase = (typeof INTEGRATION_PHASES)[number];

const PHASE_STATUS_GETTERS: Record<
  IntegrationPhase,
  (integration?: Integration) => ReturnType<typeof getOnboardingCurrentStatus>
> = {
  Onboarding: getOnboardingCurrentStatus,
  Training: getTrainingCurrentStatus,
  Development: getDevelopmentCurrentStatus,
  Validation: getValidationCurrentStatus,
};

const COMPLETE_PHASE_STATUSES = new Set([
  "Completed",
  "Not applicable",
  "Validated - Full approval",
  "Validated - Conditional approval",
]);

export const isIntegrationPhaseComplete = (
  integration: Integration,
  phase: IntegrationPhase
): boolean => {
  const status = PHASE_STATUS_GETTERS[phase](integration);
  return status != null && COMPLETE_PHASE_STATUSES.has(status);
};

export const getNextIntegrationPhase = (
  phase: IntegrationPhase
): IntegrationPhase | null => {
  const index = INTEGRATION_PHASES.indexOf(phase);
  if (index < 0 || index >= INTEGRATION_PHASES.length - 1) {
    return null;
  }
  return INTEGRATION_PHASES[index + 1];
};

type IntegrationTimelineSectionProps = {
  integration: Integration;
  selectedPhase: IntegrationPhase;
  onPhaseSelect: (phase: IntegrationPhase) => void;
};

export const resolveDefaultIntegrationPhase = (
  integration: Integration
): IntegrationPhase => {
  const statuses = [
    getOnboardingCurrentStatus(integration),
    getTrainingCurrentStatus(integration),
    getDevelopmentCurrentStatus(integration),
    getValidationCurrentStatus(integration),
  ];

  const inProgressIndex = statuses.findIndex((status) => status === "In progress");
  if (inProgressIndex >= 0) {
    return INTEGRATION_PHASES[inProgressIndex];
  }

  const activeIndex = statuses.findIndex(
    (status) =>
      status !== "Completed" &&
      status !== "Not applicable" &&
      status !== "Validated - Full approval" &&
      status !== "Validated - Conditional approval"
  );

  if (activeIndex >= 0) {
    return INTEGRATION_PHASES[activeIndex];
  }

  return "Onboarding";
};

const IntegrationTimelineSection: React.FC<IntegrationTimelineSectionProps> = ({
  integration,
  selectedPhase,
  onPhaseSelect,
}) => {
  const { canAccessTimeline } = useTimelineStepPermissions();
  const milestones = React.useMemo(
    () => buildIntegrationMilestones(integration),
    [integration]
  );
  const completionPercent = getTimelineCompletionPercent(milestones);

  if (!canAccessTimeline) {
    return null;
  }

  return (
    <div className="integration-timeline-section">
      <div className="integration-timeline-section__header">
        <div className="integration-timeline-section__heading">
          <h3 className="integration-timeline-section__title">Timeline</h3>
          <p className="integration-timeline-section__subtitle">
            Integration Pipeline Status
          </p>
        </div>
      </div>

      <div className="integration-timeline-section__content">
        <div className="integration-timeline-section__timeline-wrap">
          <IntegrationTimeline
            milestones={milestones}
            className="integration-timeline--integration-details integration-timeline--integration-tabbed"
            pendingLabel="Not Started"
            selectedLabel={selectedPhase}
            onStepSelect={(label) => onPhaseSelect(label as IntegrationPhase)}
            segmentedRail
          />
        </div>
        <div className="integration-timeline-section__meta">
          <span className="integration-timeline-section__percent">
            {completionPercent}%
          </span>
        </div>
      </div>

      <IntegrationPhasePanel
        integration={integration}
        selectedPhase={selectedPhase}
      />
    </div>
  );
};

export default IntegrationTimelineSection;
